// Génération et historique des documents d'un devis.
//
// Deux règles portées par cet écran :
//
//  - **enregistrement automatique avant génération.** Le devis en
//    cours est envoyé avec la demande de PDF. Sans ça, l'utilisateur
//    modifie un prix, clique sur « PDF », et reçoit l'ancien — le
//    défaut le plus embarrassant qu'un devis puisse avoir ;
//
//  - **on ne remplace jamais un document produit.** Chaque génération
//    crée une version. Un devis envoyé le 3 reste consultable tel
//    qu'il est parti, même après une révision le 10.

import { useCallback, useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api, fetchDocument } from '../../lib/api.js';
import { formatDateTime } from '../../lib/format.js';

const TYPE_LABELS = { devis: 'Devis client', atelier: 'Feuille de production' };

/**
 * Récupère un PDF puis l'ouvre en blob.
 *
 * Passe par `fetchBlob` du client API, et non par un `fetch` écrit ici.
 * Cette fonction contournait le client avec un chemin relatif `/api…` :
 * en développement le proxy Vite le rattrapait, en production le PDF
 * partait sur `app.mertensmec.be` et revenait en 405. Même panne que
 * celle qui bloquait la connexion, même cause.
 */
async function ouvrirOuTelecharger(path, { body, telecharger = false } = {}) {
  const { blob, filename } = await fetchDocument(path, {
    method: body === undefined ? 'GET' : 'POST',
    body,
  });

  // ── LE NOM DU FICHIER VIENT DU SERVEUR — 25 août 2026 ──
  //
  // Charles : « quand on exporte un PDF de devis, le nom du PDF doit
  // être le numéro de devis. »
  //
  // Le serveur l'envoyait DÉJÀ — `Content-Disposition: filename=
  // "2026-08-063.pdf"` — depuis toujours. Deux choses le perdaient :
  //
  //   1. `Content-Disposition` n'était pas dans `exposedHeaders` du
  //      CORS, donc le navigateur le retirait de la réponse. La requête
  //      aboutissait ; seule l'information disparaissait, ce qui est
  //      plus difficile à voir qu'un blocage ;
  //   2. un blob N'A PAS DE NOM. Ouvrir `URL.createObjectURL(blob)`
  //      fait proposer l'identifiant du blob à l'enregistrement.
  //
  // On donne donc un `File` — qui, lui, porte un nom — et on force le
  // nom sur le téléchargement via l'attribut `download`.
  const nom = filename ?? 'document.pdf';
  const fichier = new File([blob], nom, { type: 'application/pdf' });
  const url = URL.createObjectURL(fichier);

  if (telecharger) {
    const a = document.createElement('a');
    a.href = url;
    a.download = nom;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(url, '_blank', 'noopener');
  }
  // Libéré après coup : sinon le blob reste en mémoire tant que l'onglet
  // vit.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return nom;
}

export function DocumentsCard({ devisId, pieces, payload, onSaved }) {
  const toast = useToast();
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get(`/devis/${devisId}/documents`), [devisId]);
  const { data, error, loading, reload } = useResource(load);

  async function generate(kind, pieceId) {
    setBusy(kind + (pieceId ?? ''));
    try {
      if (kind === 'devis') {
        // Le devis en cours part avec la demande : le PDF correspond
        // donc toujours à ce qui est affiché.
        // « Exporter » veut dire « j'emporte le fichier » : il descend
        // dans les téléchargements, sous son numéro de devis.
        const nom = await ouvrirOuTelecharger(`/devis/${devisId}/documents/devis`, {
          body: payload, telecharger: true,
        });
        onSaved?.();
        toast.success(`Devis exporté : ${nom}`);
      } else {
        // La fiche part avec le devis en cours, comme le PDF client.
        // Elle partait VIDE : le serveur relisait la base, et la fiche
        // montrait le dernier enregistrement au lieu de l'écran. Une
        // fiche de production est un ordre de travail — elle ne doit
        // pas dépendre du souvenir d'avoir cliqué « Enregistrer ».
        const nom = await ouvrirOuTelecharger(
          `/devis/${devisId}/pieces/${pieceId}/documents/atelier`,
          { body: payload, telecharger: true },
        );
        onSaved?.();
        toast.success(`Feuille exportée : ${nom}`);
      }
      reload();
    } catch (err) {
      toast.error(err.message);
      if (err.details) {
        for (const [piece, manque] of Object.entries(err.details)) {
          toast.error(`${piece} : ${manque}`);
        }
      }
    } finally {
      setBusy(null);
    }
  }

  const columns = [
    { key: 'type', label: 'Document', render: (d) => TYPE_LABELS[d.type] ?? d.type },
    { key: 'version', label: 'Version', numeric: true, render: (d) => <Badge>v{d.version}</Badge> },
    { key: 'created_at', label: 'Généré le', render: (d) => formatDateTime(d.created_at) },
    { key: 'generated_by', label: 'Par', render: (d) => d.generated_by ?? '—' },
    {
      key: 'actions',
      label: '',
      render: (d) => (
        <span className="row row--serre">
          {/* Consulter une version : on l'ouvre. Le `File` porte son nom,
              donc l'enregistrement depuis le lecteur PDF le propose. */}
          <Button size="sm" variant="ghost" onClick={() => ouvrirOuTelecharger(`/documents/${d.id}`)}>
            Ouvrir
          </Button>
          {/* Et l'emporter, sous son numéro de devis. */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => ouvrirOuTelecharger(`/documents/${d.id}`, { telecharger: true })}
          >
            Télécharger
          </Button>
        </span>
      ),
    },
  ];

  return (
    <Card>
      <CardHead title="Documents">
        <Button
          size="sm"
          variant="primary"
          disabled={busy === 'devis'}
          onClick={() => generate('devis')}
        >
          {busy === 'devis' ? 'Génération…' : 'PDF du devis'}
        </Button>
      </CardHead>
      <CardBody className="stack">
        <p className="field__hint">
          Le devis est enregistré avant chaque génération : le PDF correspond toujours à ce qui est
          affiché. Chaque génération crée une version, aucune n’écrase la précédente.
        </p>

        {pieces.length > 0 ? (
          <div>
            <p className="caps">Feuilles de production</p>
            <div className="row row--wrap">
              {pieces.map((p) => (
                <Button
                  key={p.id ?? p.name}
                  size="sm"
                  variant="secondary"
                  disabled={!p.id || busy === `atelier${p.id}`}
                  title={p.id ? undefined : "Enregistrez le devis d'abord"}
                  onClick={() => generate('atelier', p.id)}
                >
                  {busy === `atelier${p.id}` ? '…' : p.name || p.ref || 'Pièce'}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        {loading ? (
          <SkeletonRows rows={2} />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : (
          <Table
            columns={columns}
            rows={data.documents}
            caption="Versions générées"
            empty={<EmptyState title="Aucun document">Aucun PDF n’a encore été généré.</EmptyState>}
          />
        )}
      </CardBody>
    </Card>
  );
}

export default DocumentsCard;
